import { Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren, afterNextRender, computed, inject, signal, } from '@angular/core';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { SignalsStoreService } from '../shared/signals-store.service';
import { ProductCardComponent } from '../shared/product-card/product-card.component';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { ProductsService } from '../product/products.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { toObservable } from '@angular/core/rxjs-interop';
import { auditTime, bufferWhen, filter, map, mergeMap, of, scan, startWith, Subject, switchMap, tap, timer } from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StockService } from '../stock/stock.service';
import { MEDIUM } from "../../scss/responsive/responsive";
import { HeaderService } from '../shared/header/header.service';
import { ResolutionService } from '../shared/resolution.service';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import { ShopService } from './shop.service';
import { MenuService } from '../shared/menu.service';
import { environment } from '../../environments/environment';
import { FooterComponent } from "../shared/footer/footer.component";
import { capitalizeWords, formatStringForURL } from '../shared/common/utils';
import { Product, ProductResume } from '../product/product.types';
import { ActivatedRoute } from '@angular/router';
import { SearchBarComponent } from '../shared/header/search-bar/search-bar.component';
import { FilteringTagsComponent } from '../shared/shop-header/filtering-tags/filtering-tags.component';
import { MatChipsModule } from '@angular/material/chips';
import { ProductCardV2Component } from '../shared/product-card-v2/product-card-v2.component';

@Component({
  selector: 'app-shop',
  templateUrl: './shop.component.html',
  styleUrl: './shop.component.scss',
  imports: [
    CommonModule,
    SidebarComponent,
    NgbModule,
    MatProgressBarModule,
    EmptyMessageComponent,
    FooterComponent,
    SearchBarComponent,
    FilteringTagsComponent,
    MatChipsModule,
    ProductCardV2Component
  ]
})
export class ShopComponent implements OnInit, OnDestroy {


  @ViewChildren('subcategory')
  subcategories!: QueryList<ElementRef>;

  @ViewChildren('subcategorySon')
  subcategoriesSon!: QueryList<ElementRef>;

  @ViewChild('layoutShopMain') layoutShopMain!: ElementRef;

  #productsService = inject(ProductsService);
  #headerService = inject(HeaderService);
  #resolutionService = inject(ResolutionService);
  #menuService = inject(MenuService);
  #shopService = inject(ShopService);
  #activatedRoute = inject(ActivatedRoute);
  #location = inject(Location);

  categoryBanner = computed(() => this.#shopService.categoryBanner());

  signalsStore = inject(SignalsStoreService);
  stockService = inject(StockService);

  isShowingShopFilters = computed(() => this.#headerService.displaySearchBar());
  isFilteringByTags = computed(() => this.#checkTagsFilter());

  isSidebarOpen = this.signalsStore.isSidebarOpen;
  hasSession = this.signalsStore.hasSession;
  selectedCategory = this.signalsStore.selectedCategory;

  isCollapseButtonHovered = signal(false);

  lastCategory: string | null = null;
  lastSubcategory: string | null = null;
  lastSubcategorySon: string | null = null;

  productsSignal = computed(() => {
    const sortedProducts = this.#productsService.productsSignal();

    let lastCategory: number = -1;
    let lastSubcategory: number = -1;
    let lastSubcategorySon: number = -1;

    this.lastSubcategorySon = null;
    this.lastSubcategory = null;
    this.lastCategory = null;

    return sortedProducts.map((product: any) => {

      product.subCategorySonInView = null;
      product.categoryInView = null;
      product.subCategoryInView = null;

      let categoryId = 0,
        subCategoryId = 0,
        subCategorySonId = 0;

      if (product.category) {
        categoryId = +product.category.id;
        if (categoryId !== lastCategory) {
          product.categoryInView = this.#getCategoryBanner(categoryId);
          lastCategory = categoryId;
        }
      }

      if (product.subcategory) {

        subCategoryId = +product.subcategory.id;
        if (subCategoryId !== lastSubcategory) {
          product.subCategoryInView = this.#getCategoryBanner(categoryId, subCategoryId);
          lastSubcategory = subCategoryId;
        }
      }

      if (product.subcategorySon) {
        subCategorySonId = +product.subcategorySon.id;
        if (subCategorySonId !== lastSubcategorySon) {
          product.subCategorySonInView = this.#getCategoryBanner(categoryId, subCategoryId, subCategorySonId);
          lastSubcategorySon = subCategorySonId;
        }
      }

      this.#setupProductAnchorLinks(product);

      return product as ProductResume;

    }) as ProductResume[];
  });
  categoriesSignal: any = computed(() => this.#productsService.productsSignal());
  marketStatus = computed(() => this.signalsStore.marketStatus());
  closedMarket = computed(() => {
    const ms = this.marketStatus();
    return {
      title: ms.bannerTitle || `Our Online Farmer's Market Opens ${ms.openingDay.toUpperCase()} Afternoon!`,
      legend: ms.bannerContent || `We're busy sourcing next week's availability! Check back ${capitalizeWords(ms.openingDay.toLowerCase())} to begin shopping.`
    };
  })
  sidebarChanged = toObservable(this.signalsStore.sidebarFilterChanged);

  isContentLoading = computed(() => this.stockService.isLoading() || this.#productsService.isLoading());
  isContentLoaded = computed(() => this.stockService.isLoaded() || this.#productsService.isLoaded());

  isMobile = computed(() => this.#resolutionService.isMobile());

  emptyMessage = 'No products were found.';
  hasGlobalMessages = computed(() => !!this.signalsStore.globalMessages().length);

  //#region flows config
  displayCategoryName = signal(environment.config.flows.shop.displayCategoryName);
  //#endregion

  productDetails = signal<Map<number, Product>>(new Map()); // Cache of product details
  #visibleProducts$ = new Subject<number[]>(); // IDs of products that are visible in the viewport

  filters = computed(() => this.signalsStore.getFilters());

  constructor(@Inject(PLATFORM_ID) private platformId: any) {
    afterNextRender(() => {
      const isMobile = window.innerWidth <= MEDIUM;
      if (isMobile) this.signalsStore.isSidebarOpen.set(false);
    });
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.signalsStore.setIsInShopComponent(true);
    if (this.signalsStore.filterByProducer()) {
      setTimeout(() => {
        this.#headerService.displaySearchBar.set(true);
      }, 500);
    }
    else
      this.#getProductsResolverData();

    this.#setupBatchRequests();
  }

  ngOnDestroy(): void {
    this.signalsStore.setIsInShopComponent(false);
    this.signalsStore.categoryInViewPort.set(null);
  }

  onScroll(event: any) {

    if (!this.subcategories.length)
      return;

    const contentY = document
      .getElementsByClassName('layout-shop__main')[0]
      ?.getBoundingClientRect().y ?? 0;

    if (!contentY)
      return;

    // Search the element most near when scroll is down.

    let categoryId = null;
    let subCategoryId = null;
    let subCategorySonId = null;

    // Validate if the scroll is bottom

    let element: ElementRef<any> | undefined;
    const isBottom = event.srcElement.scrollHeight - event.srcElement.scrollTop <= event.srcElement.clientHeight + 5;

    if (isBottom) {
      element = this.subcategories.toArray().reverse()[0];
      if (+(element?.nativeElement?.getAttribute('sub-category-son-id') || 0) !== -1)
        element = this.subcategoriesSon.toArray().reverse()[0];
    }
    else {

      element = this.subcategoriesSon
        .find(x => {
          const diffY = x.nativeElement.getBoundingClientRect().y - contentY;

          if (diffY < 0) return false;
          return diffY >= -300 && diffY <= 100;
        });

      if (!element) {
        element = this.subcategories
          .find(x => {
            const diffY = x.nativeElement.getBoundingClientRect().y - contentY;

            if (diffY < 0) return false;
            return diffY >= -300 && diffY <= 100;
          });
      }

      if (!element) {

        // Search the last element most near when scroll is up.

        const sub = this.subcategories
          .toArray()
          .reverse()
          .map(x => {
            const diffY = x.nativeElement.getBoundingClientRect().y - contentY;
            return {
              ...x,
              diffY
            };
          })
          .find(x => {
            return x.diffY < 0;
          });

        const subson = this.subcategoriesSon
          .toArray()
          .reverse()
          .map(x => {
            const diffY = x.nativeElement.getBoundingClientRect().y - contentY;
            return {
              ...x,
              diffY
            };
          })
          .find(x => {
            return x.diffY < 0;
          });


        const absSub = Math.abs(sub?.diffY || 0)
        const absSubSon = Math.abs(subson?.diffY || 0);

        const absSubDiff = Math.abs(absSub - 200);
        const absSubSonDiff = Math.abs(absSubSon - 200);

        element = absSubDiff >= absSubSonDiff ? subson : sub;

      }
    }

    if (!element)
      return;

    // Get the category and subcategory attibutes

    categoryId = +element.nativeElement.getAttribute('category-id');
    subCategoryId = +element.nativeElement.getAttribute('sub-category-id');
    subCategorySonId = +element.nativeElement.getAttribute('sub-category-son-id');

    const viewportAnchorLink = element.nativeElement.getAttribute('shop-data-anchor-link');
    if (viewportAnchorLink) this.changeAnchorLink(viewportAnchorLink);

    // If yet not change of category, don't do nothing.

    if (this.signalsStore.categoryInViewPort()?.categoryId === categoryId &&
      this.signalsStore.categoryInViewPort()?.subCategoryId === subCategoryId &&
      this.signalsStore.categoryInViewPort()?.subCategorySonId === subCategorySonId)
      return;

    // Emit the new visible category.

    this.signalsStore
      .categoryInViewPort
      .set({
        categoryId,
        subCategoryId,
        subCategorySonId
      });
  }

  #getProductsResolverData() {
    this.#productsService.getAllProductsResume(true).pipe(
      tap(() => {
        this.scrollIntoView();
        this.#setInitialCategoryInViewPort();
      })
    ).subscribe();
  }

  scrollIntoView() {
    const selectedProductId = localStorage.getItem('selectedProductId');
    if (selectedProductId != null) {
      this.#goToSelectedProduct(selectedProductId);
    } else {
      this.#handleRouteChanges();
    }
  }

  #goToSelectedProduct(selectedProductId: string) {
    setTimeout(() => {
      const scrollToTop = document.getElementById(selectedProductId!.toString());
      if (scrollToTop) {

        const parent = document.getElementById('layoutShopMain');
        parent?.scrollTo({
          top: (scrollToTop.offsetTop - 100),
          left: 0,
          behavior: 'instant'
        });
      }
      localStorage.removeItem('selectedProductId');
    }, 1);
  }

  toggleSidebar() {
    this.signalsStore.isSidebarOpen.update((currentValue) => !currentValue);
  }

  toggleCollapseBarHover(hovered: boolean) {
    this.isCollapseButtonHovered.set(hovered);
  }

  getCategoryBanner(categoryId: number): { banner: string, url: string } | null {

    const category = this.#menuService.menu().find(x => x.id === categoryId);

    const banner = category?.banners[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner) return null;

    return {
      banner,
      url: category.banners.url
    };
  }

  #getCategoryBanner(categoryId: number, subCategoryId?: number, subCategorySonId?: number) {

    let item = this.signalsStore.menuSignal()
      .find(x => x.id === categoryId);

    if (subCategoryId)
      item = item?.subCategories.find((x: any) => x.id === subCategoryId);

    if (subCategorySonId)
      item = item?.subCategories.find((x: any) => x.id === subCategorySonId);

    return this.#validateBanner(item);
  }

  #validateBanner(item: any): { banner: string, url: string, show: boolean } {

    const banner = item?.banners[this.isMobile() ? 'mobile' : 'desktop'];

    if (!banner)
      return { banner: '', url: '', show: true };

    return {
      banner,
      url: item.banners.url,
      show: true
    };
  }

  #checkTagsFilter() {
    return this.#productsService.tagsSignal().some(tag => tag.isChecked());
  }

  #setupBatchRequests() {
    const noActivity$ = this.#visibleProducts$.pipe(
      switchMap(() => timer(100).pipe(map(() => true))), // Si no hay nuevos valores en 500ms → inactividad
      startWith(false) // Al inicio hay actividad
    );

    this.#visibleProducts$.pipe(
      bufferWhen(() => noActivity$.pipe(filter(inactive => inactive))), // Espera a que haya inactividad
      filter(productGroups => productGroups.length > 0), // Evita procesar arrays vacíos
      mergeMap(productGroups => productGroups.flat()), // 🔄 Aplana el array → number[]
      scan((acc: number[], productId: number) => [...new Set([...acc, productId])], []), // 📌 Acumula productos únicos
      auditTime(500), // ⏳ Espera estabilidad antes de hacer la petición
      switchMap(productIds => {
        const idsToFetch = productIds.filter(id => !this.productDetails().has(id));
        return idsToFetch.length > 0 ? this.#productsService.getProductsDetailsByIds(idsToFetch) : of({});
      })
    ).subscribe(details => {
      this.productDetails.update(prev => {
        const updatedMap = new Map(prev); // Clonar el Map existente
        Object.entries(details).forEach(([index, detail]) => {
          const productDetail: Product = detail as Product;
          updatedMap.set(Number(productDetail.id), productDetail as Product); // Convertir id a número y agregarlo
        });
        return updatedMap;
      });
    });
  }

  onProductCardReady(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  onProductCardHover(productId: number) {
    if (this.productDetails().has(productId)) return;
    else this.#visibleProducts$.next([productId]);
  }

  removeChipFilter(key: string) {
    if (key && this.filters().has(key)) {
      const type = this.filters().get(key)?.type;
      if (!type) return;

      this.signalsStore.setFilterChipRemoved(key);
    }
  }

  #setupProductAnchorLinks(product: ProductResume) {
    const anchorLinkArray = ['shop'];

    if (product.category.name) {
      anchorLinkArray.push(formatStringForURL(product.category.name));
      const categoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.categoryAnchorLink = categoryAnchorLink;
    }

    if (product.subcategory?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategory.name));
      const subcategoryAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategoryAnchorLink = subcategoryAnchorLink;
    }

    if (product.subcategorySon?.name) {
      anchorLinkArray.push(formatStringForURL(product.subcategorySon.name));
      const subcategorySonAnchorLink = `/${anchorLinkArray.join('/')}`;
      product.subcategorySonAnchorLink = subcategorySonAnchorLink;
    }
  }

  #handleRouteChanges() {
    setTimeout(() => {
      const category = this.#activatedRoute.snapshot.paramMap.get('category');
      const subcategory = this.#activatedRoute.snapshot.paramMap.get('subcategory');
      const subcategorySon = this.#activatedRoute.snapshot.paramMap.get('slug');
      const routeArray = [];
      if (category) routeArray.push(category);
      if (subcategory) routeArray.push(subcategory);
      if (subcategorySon) routeArray.push(subcategorySon);

      const anchorLink = `/shop/${routeArray.join('/')}`;

      const element: any = document.querySelector(`[shop-data-anchor-link="${anchorLink}"]`);
      if (!element) {
        return
      };
      const parent = document.getElementById('layoutShopMain');
      parent?.scrollTo({
        top: (element.offsetTop),
        left: 0,
        behavior: 'instant'
      });


    }, 1);
  }

  changeAnchorLink(anchorLink: string) {
    this.#location.replaceState(anchorLink);
  }

  #setInitialCategoryInViewPort() {
    setTimeout(() => {
      // Solo ejecutar si la ruta actual es exactamente "/shop" (sin parámetros adicionales)
      const currentPath = this.#location.path();
      if (currentPath !== '/shop') {
        return;
      }

      const products = this.productsSignal();
      if (products && products.length > 0) {
        const firstProduct = products[0];

        if (firstProduct.category) {
          const categoryId = +firstProduct.category.id;
          const subCategoryId = firstProduct.subcategory ? +firstProduct.subcategory.id : 0; // Usar 0 como valor por defecto
          const subCategorySonId = firstProduct.subcategorySon ? +firstProduct.subcategorySon.id : undefined;

          // Construir la URL anchor link usando los anchor links ya generados
          let anchorLink = '/shop';
          if (firstProduct.subcategorySonAnchorLink) {
            anchorLink = firstProduct.subcategorySonAnchorLink;
          } else if (firstProduct.subcategoryAnchorLink) {
            anchorLink = firstProduct.subcategoryAnchorLink;
          } else if (firstProduct.categoryAnchorLink) {
            anchorLink = firstProduct.categoryAnchorLink;
          }

          // Cambiar la URL
          this.changeAnchorLink(anchorLink);

          // Establecer categoryInViewPort
          this.signalsStore.categoryInViewPort.set({
            categoryId,
            subCategoryId,
            subCategorySonId
          });
        }
      }
    }, 1000);
  }


}
